import { Box, Flex, useBreakpointValue } from '@gazin/ecommerce-ui'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import {
  startTransition,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import BarraFiltrosProdutos from 'src/components/barraFiltrosProdutos'
import { ProductList } from 'src/components/conteudoBusca/Content'
import { hasSeoContent, SeoContent } from 'src/components/seoContent'
import facebookActions from 'src/components/facebookPixel/facebookActions'
import FiltersProductsMobile from 'src/components/filtersProductsMobile'
import { LoadDepartaments } from 'src/components/loadDepartments'
import { FIRST_PAGE } from 'src/components/Pagination/PaginationGroupItems'
import { mapToItemProdutoVertical } from 'src/components/productVertical/utils'
import { CategoryReponse } from 'src/services/marketplace-api/dtos'
import { searchProductsCategory } from 'src/services/marketplace-api/searchCategory'
import { searchProducts as fetchSearchProducts } from 'src/services/marketplace-api/searchProducts'
import {
  Filter,
  Pagination,
  Product,
  SearchProductsResponse,
  SelectedFilters,
  Sort,
  Suggestion,
} from 'src/services/searchProducts/dtos/search-products-response'
import { ProductsSortingKey } from 'src/util/enums/products-sorting-key'
import { identifyFilter } from 'src/util/searchAndCategory/identifyFilter'
import { afterNextPaint } from 'src/util/afterNextPaint'
import * as gtm from '../../services/gtm'

/** Chakra UI 'lg' breakpoint — aligns with isMobileDevice: { base: true, md: true, lg: false } */
const MOBILE_BREAKPOINT_PX = 992

type ConfigPaginas = {
  firstPage: number
  currentPage: number
  nextPage: number
  lastPage: number
  prevPage: number
}

type MobileStateCache = {
  produtos: Product[]
  pagina: number
  configPaginas: ConfigPaginas
  productsSize: number
  itemsPerPage: number
}

function getMobileStateKey(
  searchTerm: string,
  filterKey: string,
  sortKey: string
): string {
  return `mobile-products:${searchTerm}:${filterKey}:${sortKey}`
}

function saveMobileState(state: MobileStateCache, key: string): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(state))
  } catch {
    // sessionStorage unavailable (private mode, quota exceeded, etc.) — fail silently
  }
}

function loadMobileState(key: string): MobileStateCache | null {
  try {
    const raw = sessionStorage.getItem(key)
    return raw ? (JSON.parse(raw) as MobileStateCache) : null
  } catch {
    return null
  }
}

function clearMobileState(key: string): void {
  try {
    sessionStorage.removeItem(key)
  } catch {
    // fail silently
  }
}

function parsePaginationCursor(value: string | undefined): number | undefined {
  if (!value) return undefined
  const direct = Number(value)
  if (direct > 0) return direct
  const fromQuery = Number(new URLSearchParams(value).get('page'))
  return fromQuery > 0 ? fromQuery : undefined
}

const SearchNotFound = dynamic(
  () =>
    import('src/components/searchNotFound').then(
      (module) => module.SearchNotFound
    ),
  { ssr: false }
)

export type SearchContent = {
  categorySlug?: string
  category?: CategoryReponse | undefined
  isCategoryPage: boolean
  results: SearchProductsResponse
}

export type FilterProduct = {
  attribute?: string
  descricao?: string
  id_atributo: number | string
  id_valor_unidade: number | string
  label: string
  queryFiltro?: string
  tipo: string
  valor_maximo?: string | number | null
  valor_minimo?: string | number | null
}

/**
 * Marks `selected: true` on filter values that match the active URLFilter entries.
 * URLFilter entries come from route.query.filtro (already without the "filtro=" prefix).
 *   Discrete  : "d:{facet}:{label}"
 *   Continuous: "c:{facet}:{label}:{min}:{max}"
 * Values are matched by value.idO === facet && String(value.id) === label.
 */
function markSelectedFilterValues(
  filters: Filter[],
  urlFilter: string[]
): Filter[] {
  if (!urlFilter?.length) return filters

  const activeDiscrete = new Map<string, Set<string>>()

  for (const entry of urlFilter) {
    const raw = entry.startsWith('filtro=') ? entry.slice(7) : entry
    const parts = raw.split(':')
    if (parts[0] === 'd' && parts.length >= 3) {
      const facet = parts[1]
      const label = parts.slice(2).join(':')
      if (!activeDiscrete.has(facet)) activeDiscrete.set(facet, new Set())
      activeDiscrete.get(facet)!.add(label)
    }
  }

  if (!activeDiscrete.size) return filters

  return filters.map((filter) => ({
    ...filter,
    values: filter.values.map((value) => {
      const facet = value.idO
      const id = String(value.id ?? '')
      const isActive = facet
        ? activeDiscrete.get(facet)?.has(id) ?? false
        : false
      return isActive ? { ...value, selected: true } : value
    }),
  }))
}

export default function SearchAndCategoryPage({
  category,
  isCategoryPage,
  results,
}: SearchContent) {
  const [isGrid, setIsGrid] = useState(true)
  const [pagina, setPagina] = useState(FIRST_PAGE)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const [configPaginas, setConfigPaginas] = useState({
    firstPage: 1,
    currentPage: 1,
    nextPage: 1,
    lastPage: 1,
    prevPage: 1,
  })
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters[]>()
  const [produtos, setProdutos] = useState<Product[]>(results?.products)
  const [productsSize, setProductsSize] = useState<number>(results?.size)
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    results?.itemsPerPage
  )
  const [suggestions, setSuggestions] = useState<Suggestion[] | undefined>(
    results?.suggestions
  )
  const [loading, setLoading] = useState(!results?.products?.length)
  const [selectedFilter, setSelectedFilter] = useState<Partial<FilterProduct>>({
    descricao: '',
    id_atributo: '',
    id_valor_unidade: '',
    label: '',
    queryFiltro: '',
    tipo: '',
    valor_maximo: '',
    valor_minimo: '',
  })
  const [filters, setFilters] = useState<Filter[]>(results?.filters)
  const [sort, setSort] = useState<Sort[]>(results?.sort ?? [])
  const [arrayFilters, setArrayFilters] = useState<Partial<FilterProduct>[]>([])
  const [parametros] = useState<{
    filtro: string
    ordenacao: ProductsSortingKey | string
  }>({
    filtro: '',
    ordenacao: ProductsSortingKey.Relevance,
  })
  const isFiltersEnabled = true
  const hasMountedRef = useRef(false)
  const lastFetchedQueryRef = useRef<string | null>(null)

  const route = useRouter()

  const URLFilter: string[] = Array.isArray(route.query.filtro)
    ? route.query.filtro
    : route.query.filtro
    ? [route.query.filtro]
    : []
  const defaultSortBy =
    sort?.find((sortOption) => sortOption.selected)?.name ??
    ProductsSortingKey.Relevance
  const queryOrder = (route.query.sortBy as string) || defaultSortBy
  const searchQuery = route?.query?.busca?.[0] ?? ''
  const queryPage = Number(route.query.pagina) || 1

  const ssrPageRef = useRef(queryPage)

  const isMobileDevice = useBreakpointValue(
    { base: true, md: true, lg: false },
    { fallback: 'lg' }
  )

  const defineTotalPages = useCallback(
    (pagination?: Pagination, currentPage?: number) => {
      const lastPageNumber = parsePaginationCursor(pagination?.last)
      const nextPageNumber = parsePaginationCursor(pagination?.next)
      const firstPageNumber = parsePaginationCursor(pagination?.first)

      const totalPaginas =
        firstPageNumber ?? nextPageNumber ?? lastPageNumber ?? pagina

      const previousPage = (currentPage ?? totalPaginas) - 1

      setPagina(currentPage ?? totalPaginas)
      setConfigPaginas((prevConfigPaginas) => ({
        ...prevConfigPaginas,
        firstPage: firstPageNumber ?? 1,
        currentPage: currentPage ?? 1,
        nextPage: nextPageNumber ?? 1,
        lastPage: lastPageNumber ?? 1,
        prevPage: previousPage ?? 1,
      }))
    },
    [pagina]
  )

  const searchProducts = useCallback(
    async (page: number, products?: Product[]) => {
      const isMobile = window.innerWidth < MOBILE_BREAKPOINT_PX

      if (!isMobile || page === FIRST_PAGE) {
        setLoading(true)
      } else {
        setIsFetchingMore(true)
      }

      const productsInMemory = products === undefined ? produtos : products

      const query = isCategoryPage
        ? route.query.categoria?.[0]
        : route.query.busca?.[0]

      if (!query) return setLoading(false)

      try {
        const pageData = isCategoryPage
          ? await searchProductsCategory({
              search: query,
              filter: URLFilter,
              order: queryOrder,
              page,
            })
          : await fetchSearchProducts({
              search: query,
              filter: URLFilter,
              order: queryOrder,
              page,
            })

        if (!isCategoryPage) {
          facebookActions.search(route.query.busca?.[0], produtos)
        }

        setSuggestions(pageData?.suggestions)

        if (!pageData?.products) {
          setPagina(FIRST_PAGE)
          defineTotalPages(undefined, FIRST_PAGE)
          setProdutos([])
          return
        }

        if (pageData.filters) {
          setFilters(markSelectedFilterValues(pageData.filters, URLFilter))
        }

        if (pageData.sort?.length) setSort(pageData.sort)

        if (pageData.selectedFilters)
          setSelectedFilters(pageData.selectedFilters)

        const productsToRender = isMobile
          ? productsInMemory.concat(pageData.products)
          : pageData.products

        setProdutos(productsToRender)
        setProductsSize(pageData.size)
        setItemsPerPage(pageData.itemsPerPage)
        setPagina(page)
        defineTotalPages({ ...pageData.pagination }, page)

        if (isMobile && page > FIRST_PAGE) {
          const searchTerm = isCategoryPage
            ? route.query.categoria?.[0] ?? ''
            : route.query.busca?.[0] ?? ''
          const filterKey = URLFilter.slice().sort().join(',')
          const sortKey = queryOrder ?? ''
          const cacheKey = getMobileStateKey(searchTerm, filterKey, sortKey)

          const lastPage =
            parsePaginationCursor(pageData.pagination?.last) ??
            configPaginas.lastPage
          const nextPage =
            parsePaginationCursor(pageData.pagination?.next) ?? page + 1
          const firstPage =
            parsePaginationCursor(pageData.pagination?.first) ?? FIRST_PAGE

          saveMobileState(
            {
              produtos: productsToRender,
              pagina: page,
              configPaginas: {
                firstPage,
                currentPage: page,
                nextPage,
                lastPage,
                prevPage: page - 1,
              },
              productsSize: pageData.size,
              itemsPerPage: pageData.itemsPerPage,
            },
            cacheKey
          )
        }
      } catch (error) {
        // Revert the query key so the user can retry by clicking "Ver mais" again
        lastFetchedQueryRef.current = null
      } finally {
        setLoading(false)
        setIsFetchingMore(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      URLFilter,
      defineTotalPages,
      isCategoryPage,
      pagina,
      produtos,
      queryOrder,
      route.query.busca,
      route.query.categoria,
    ]
  )

  function changeSearchRoute(queryParams: string) {
    const urlPathName = isCategoryPage
      ? `/categoria/${route.query.categoria?.[0]}`
      : `/busca/${route.query.busca?.[0]}`

    const nextUrl = queryParams ? `${urlPathName}?${queryParams}` : urlPathName

    // TNDC-397: push dispara routeChangeStart → setState no _app em prioridade
    // síncrona; dentro do clique isso re-renderizava o app inteiro (INP).
    afterNextPaint(() => route.push(nextUrl, undefined, { shallow: true }))
  }

  function changeOrdination(ordination: string) {
    startTransition(() => applyOrdination(ordination))
  }

  function applyOrdination(ordination: string) {
    setLoading(true)
    setPagina(FIRST_PAGE)
    const searchParams = new URLSearchParams()

    if (arrayFilters?.length) {
      arrayFilters.forEach((filtro) => {
        if (!filtro.queryFiltro) return

        const rawFilter = filtro.queryFiltro.startsWith('filtro=')
          ? filtro.queryFiltro.slice(7)
          : filtro.queryFiltro

        searchParams.append('filtro', rawFilter)
      })
    }

    if (ordination) {
      searchParams.set('sortBy', ordination)
    }

    changeSearchRoute(searchParams.toString())
  }

  const changeURL = (selectedFilters: Partial<FilterProduct>[]) => {
    if (selectedFilters) {
      let queryParamsFiltro = ''
      const filterSelectedWithDescription = selectedFilters.reduce(
        (acc, filtro) => {
          if (!filtro.descricao || !filtro.queryFiltro) return acc

          if (filtro.tipo == 'continuous') {
            acc[filtro.descricao] = filtro.queryFiltro
            return acc
          }

          const currentValue = acc[filtro.descricao]
          const currentArray = Array.isArray(currentValue) ? currentValue : []

          acc[filtro.descricao] = [...currentArray, filtro.queryFiltro]
          return acc
        },
        {} as Record<string, string | string[]>
      )

      const returnedValueObject = Object.entries(
        filterSelectedWithDescription
      ).reduce((acc, [_, value]) => {
        if (Array.isArray(value)) {
          value.map((item: any) => {
            return acc.push(item as never)
          })
          return acc
        }
        acc.push(value as never)
        return acc
      }, [])

      queryParamsFiltro = returnedValueObject.join('&')

      const currentSortBy =
        (route.query.sortBy as string | undefined) ||
        new URLSearchParams(window.location.search).get('sortBy') ||
        undefined
      if (currentSortBy) {
        queryParamsFiltro = queryParamsFiltro
          ? `${queryParamsFiltro}&sortBy=${currentSortBy}`
          : `sortBy=${currentSortBy}`
      }

      changeSearchRoute(queryParamsFiltro)
    }
  }

  function clearFilters() {
    clearSelectedFilter()
    setArrayFilters([])
    changeURL([])
  }

  function clearSelectedFilter() {
    setSelectedFilters(undefined)
    setSelectedFilter({
      tipo: '',
      id_atributo: '',
      id_valor_unidade: '',
      valor_minimo: '',
      valor_maximo: '',
      descricao: '',
      label: '',
      queryFiltro: '',
    })
  }

  function removeFilter(index: number | any) {
    if (!arrayFilters?.length) return

    if (typeof index !== 'number') {
      const newArrayFilters = arrayFilters.filter(
        (item) =>
          !(
            item.id_atributo === index?.id_atributo &&
            item.label === index?.label
          )
      )

      setArrayFilters(newArrayFilters)

      changeURL(newArrayFilters)
      return
    }

    const removedFilter = arrayFilters.splice(index, 1)?.[0]
    const removedSelectedFilter =
      removedFilter?.id_atributo === selectedFilter?.id_atributo &&
      removedFilter?.id_valor_unidade === selectedFilter?.id_valor_unidade

    if (removedSelectedFilter) {
      clearSelectedFilter()
    }

    setArrayFilters(arrayFilters)

    changeURL(arrayFilters)
  }

  function selectFilter(filter: FilterProduct) {
    startTransition(() => applyFilter(filter))
  }

  function applyFilter({
    attribute,
    id_atributo,
    id_valor_unidade,
    label,
    tipo,
    valor_maximo,
    valor_minimo,
  }: FilterProduct) {
    setLoading(true)
    let selectedFilters = arrayFilters || []
    let queryFiltro = ''
    let filterInformation: Partial<FilterProduct> = {}

    switch (tipo) {
      case 'discrete':
        queryFiltro = `filtro=d:${id_atributo}:${id_valor_unidade}`

        filterInformation = {
          tipo,
          id_atributo,
          id_valor_unidade,
          valor_minimo: null,
          valor_maximo: null,
          descricao: attribute,
          label,
          queryFiltro,
        }

        setSelectedFilter(filterInformation)
        break

      case 'continuous':
        queryFiltro = `filtro=c:${id_atributo}:${id_valor_unidade}:${valor_minimo}:${valor_maximo}`

        filterInformation = {
          tipo,
          id_atributo,
          id_valor_unidade,
          valor_minimo,
          valor_maximo,
          descricao: attribute,
          label: '',
          queryFiltro,
        }

        setSelectedFilter(filterInformation)
        break

      default:
        queryFiltro = ''
        setSelectedFilter({
          tipo: '',
          id_atributo: '',
          id_valor_unidade: '',
          valor_minimo: '',
          valor_maximo: '',
          descricao: '',
          label: '',
          queryFiltro,
        })

        selectedFilters = []
        setArrayFilters(selectedFilters)
        break
    }

    const filterSelected = arrayFilters
      ? arrayFilters.find(
          (filtro) => filtro.queryFiltro == filterInformation.queryFiltro
        )
      : null

    if (!filterSelected) {
      setPagina(FIRST_PAGE)

      selectedFilters.push(filterInformation)
      setArrayFilters(selectedFilters)

      return changeURL(selectedFilters)
    }
  }

  const selectFilterMobile = (filters: any[]) => {
    startTransition(() => applyFiltersMobile(filters))
  }

  const applyFiltersMobile = (filters: any[]) => {
    setLoading(true)

    const filterMapped = filters.map((item) => {
      if (item.tipo === 'discrete') {
        return {
          tipo: item.tipo,
          id_atributo: item.id_atributo,
          id_valor_unidade: item.id_valor_unidade,
          valor_minimo: null,
          valor_maximo: null,
          descricao: item.descricao || item.attribute,
          label: item.label,
          queryFiltro: `filtro=d:${item.id_atributo}:${item.id_valor_unidade}`,
        }
      }

      if (item.tipo === 'continuous') {
        return {
          tipo: item.tipo,
          id_atributo: item.id_atributo,
          id_valor_unidade: item.id_valor_unidade,
          valor_minimo: null,
          valor_maximo: null,
          descricao: item.descricao || item.attribute,
          label: item.label,
          queryFiltro: `filtro=c:${item.id_atributo}:${item.id_valor_unidade}:${item.valor_minimo}:${item.valor_maximo}`,
        }
      }

      return {
        tipo: '',
        id_atributo: '',
        id_valor_unidade: '',
        valor_minimo: '',
        valor_maximo: '',
        descricao: '',
        label: '',
        queryFiltro: '',
      }
    })

    changeURL(filterMapped)
  }

  useEffect(() => {
    if (!route.isReady) return
    if (route.query.sortBy) return
    if (new URLSearchParams(window.location.search).has('sortBy')) return

    const [path, queryString = ''] = route.asPath.split('?')
    const searchParams = new URLSearchParams(queryString)

    searchParams.set('sortBy', defaultSortBy)

    const newUrl = `${path}?${searchParams.toString()}`

    window.history.replaceState(
      { ...window.history.state, as: newUrl, url: newUrl },
      '',
      newUrl
    )
  }, [defaultSortBy, route.asPath, route.isReady, route.query.sortBy])

  useEffect(() => {
    if (loading) {
      return
    }
    window.scrollTo(0, 0)
  }, [loading])

  useEffect(() => {
    if (queryPage === ssrPageRef.current) {
      defineTotalPages(results?.pagination, queryPage)
    }
    identifyFilter({
      setArrayFilters,
      setSelectedFilter,
      URLFilter,
      filters,
      results,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryPage])

  useEffect(() => {
    const isFirstPage = (route.query.pagina ?? 1) === 1
    const searchTerm = isCategoryPage
      ? route.query.categoria?.[0]
      : route.query.busca?.[0]

    if (!searchTerm) {
      setLoading(false)
      return
    }

    const filterKey = URLFilter.slice().sort().join(',')
    const sortKey = queryOrder ?? ''
    const queryKey = `${searchTerm}:${
      route.query.pagina ?? 1
    }:${filterKey}:${sortKey}`

    // Na primeira montagem, trata dados SSR e cache mobile para evitar re-fetch desnecessário.
    if (!hasMountedRef.current) {
      hasMountedRef.current = true

      if (!isFirstPage && window.innerWidth < MOBILE_BREAKPOINT_PX) {
        const cacheKey = getMobileStateKey(searchTerm, filterKey, sortKey)
        const cached = loadMobileState(cacheKey)
        if (cached) {
          setProdutos(cached.produtos)
          setPagina(cached.pagina)
          setConfigPaginas(cached.configPaginas)
          setProductsSize(cached.productsSize)
          setItemsPerPage(cached.itemsPerPage)
          lastFetchedQueryRef.current = queryKey
          setLoading(false)
          return
        }
      }

      // Se houver dados SSR e não houver filtros ativos, usa os dados do servidor
      // sem refazer a chamada à API.
      if (results?.products?.length && !URLFilter.length) {
        lastFetchedQueryRef.current = queryKey
        defineTotalPages(results?.pagination, queryPage)
        setLoading(false)
        return
      }
    }

    if (isFirstPage && window.innerWidth < MOBILE_BREAKPOINT_PX) {
      const cacheKey = getMobileStateKey(searchTerm, filterKey, sortKey)
      clearMobileState(cacheKey)
    }

    // Evita re-fetch da mesma query (hidratação do router ou re-renders)
    if (lastFetchedQueryRef.current === queryKey) return

    lastFetchedQueryRef.current = queryKey
    searchProducts(queryPage, isFirstPage ? [] : undefined)
    identifyFilter({
      setArrayFilters,
      setSelectedFilter,
      URLFilter,
      filters,
      results,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.query])

  useEffect(() => {
    identifyFilter({
      setArrayFilters,
      setSelectedFilter,
      URLFilter,
      filters,
      results,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, results])

  useEffect(() => {
    gtm.impressions(produtos)
  }, [produtos])

  const seoContent = hasSeoContent(category?.conteudo) ? (
    <Flex
      justifyContent="space-between"
      marginY="4.5rem"
      marginX={{ base: '0.75rem', lg: 0, xl: 0 }}
    >
      {/* key: o bloco segue montado durante o loading da troca de categoria, então
          sem ela expandido/overflows vazam para a próxima. TNDC-427, review !609. */}
      <SeoContent key={category?.slug} content={category?.conteudo} />
    </Flex>
  ) : null

  // O conteúdo SEO acompanha o estado sem resultado: é conteúdo da categoria,
  // não da listagem — TNDC-404, Req. 01.
  if (!produtos?.length && !loading) {
    return (
      <>
        <SearchNotFound search={searchQuery} />
        {seoContent}
      </>
    )
  }

  return (
    <>
      <Box
        alignItems="center"
        display={{ base: 'initial', lg: 'flex' }}
        flex="1"
        justifyContent="center"
      >
        <Box
          backgroundColor={{ base: '#f4f4f7', lg: '#ffffff' }}
          display={{ base: 'initial', lg: 'flex' }}
          flex="1"
          flexDirection="column"
          maxWidth={{ base: '81.875rem', '2xl': '120rem' }}
          paddingTop={{ base: '1.5rem', xl: '2rem' }}
          paddingX={{ base: '0.9375rem', '2xl': '1.875rem' }}
        >
          {loading && !produtos?.length ? (
            <Flex paddingX={{ base: '0.75rem', lg: 0, xl: 0 }} width="100%">
              <LoadDepartaments isMobile={!!isMobileDevice} />
            </Flex>
          ) : (
            <>
              <Box
                alignItems="flex-start"
                aria-busy={loading}
                display={{ base: 'flex', lg: 'grid' }}
                flex="1"
                flexDirection={{ base: 'column', lg: 'row' }}
                gridColumnGap={{ lg: '1rem', xl: '1.5rem' }}
                gridTemplateColumns={
                  isFiltersEnabled
                    ? {
                        lg: '19.375rem minmax(0, 1fr)',
                        xl: '21.875rem minmax(0, 1fr)',
                        '2xl': '23.75rem minmax(0, 1fr)',
                      }
                    : 'minmax(0, 1fr)'
                }
                justifyContent="center"
                marginX={{ base: '0.75rem', lg: 0, xl: 0 }}
                opacity={loading ? 0.6 : 1}
                paddingBottom={{
                  base: '1rem',
                  xl: category?.conteudo ? '0' : '3.9375rem',
                }}
                transition="opacity 0.2s"
              >
                {isFiltersEnabled && (
                  <>
                    <Box
                      display={{ base: 'none', lg: 'block' }}
                      minW={0}
                      overflow="hidden"
                    >
                      <BarraFiltrosProdutos
                        arrayFilters={arrayFilters}
                        clearFilters={clearFilters}
                        filters={filters}
                        removeFilter={removeFilter}
                        selectedFilters={selectedFilters}
                        selectFilter={selectFilter}
                      />
                    </Box>
                    <Box display={{ base: 'block', lg: 'none' }} width="100%">
                      <FiltersProductsMobile
                        arrayFilters={arrayFilters}
                        changeOrdination={changeOrdination}
                        clearFilters={clearFilters}
                        filters={filters}
                        appliedFilters={selectedFilters}
                        selectFilter={selectFilterMobile}
                        sort={sort}
                      />
                    </Box>
                  </>
                )}
                <ProductList
                  alterarOrdenacao={changeOrdination}
                  configPaginas={configPaginas}
                  isCategoryPage={isCategoryPage}
                  isFetchingMore={isFetchingMore}
                  isGrid={isGrid}
                  itemsPerPage={itemsPerPage}
                  mapProductToProductVertical={mapToItemProdutoVertical}
                  pagina={pagina}
                  parametros={parametros}
                  produtos={produtos}
                  resultsSize={productsSize}
                  setIsGrid={setIsGrid}
                  sort={sort}
                  suggestedSearches={suggestions}
                />
              </Box>
            </>
          )}
          {/* Fora do ternário: sem produtos o servidor renderiza o skeleton, e o SEO
              precisa estar no HTML inicial (Req. 05). TNDC-427. */}
          {seoContent}
        </Box>
      </Box>
    </>
  )
}
